import { Injectable, inject } from '@angular/core';
import { Observable, distinctUntilChanged } from 'rxjs';

import { BaseSignalQuery } from '../signal-state.util';

import { CurrentUserState, CurrentUserStore } from './current-user.store';

@Injectable({ providedIn: 'root' })
export class CurrentUserQuery extends BaseSignalQuery<CurrentUserState> {
  constructor() {
    const store = inject(CurrentUserStore);
    super(store);
  }

  override select(): Observable<CurrentUserState>;
  override select<R>(projector: (state: CurrentUserState) => R): Observable<R>;
  override select<R>(
    projector?: (state: CurrentUserState) => R
  ): Observable<CurrentUserState | R> {
    if (projector) return super.select(projector);

    return super.select().pipe(distinctUntilChanged(this.hasSameBusinessState));
  }

  private readonly hasSameBusinessState = (
    previous: CurrentUserState,
    current: CurrentUserState
  ): boolean => {
    const { avatar: _previousAvatar, organization: previousOrganization, ...previousData } =
      previous?.data || {};
    const { avatar: _currentAvatar, organization: currentOrganization, ...currentData } =
      current?.data || {};

    return (
      this.shallowEqual(previousData, currentData) &&
      this.shallowEqualOrganization(previousOrganization, currentOrganization)
    );
  };

  private shallowEqualOrganization(previous: any, current: any): boolean {
    if (previous === current) return true;

    const { logo: _previousLogo, ...previousData } = previous || {};
    const { logo: _currentLogo, ...currentData } = current || {};
    return this.shallowEqual(previousData, currentData);
  }

  private shallowEqual(previous: Record<string, any>, current: Record<string, any>): boolean {
    const previousKeys = Object.keys(previous);
    const currentKeys = Object.keys(current);
    return (
      previousKeys.length === currentKeys.length &&
      previousKeys.every(key => previous[key] === current[key])
    );
  }
}
